import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import { setMeta } from "@synthesis/shared-services/project-gge/projectMetaController/client";
import { getProjectMetaQueryKey } from "@synthesis/shared-services/project-gge/projectMetaController/hooks";
import type { ProjectMetaDto } from "@synthesis/shared-services/project-gge/projectMetaController/types";
import { updateProject } from "@synthesis/shared-services/project-gge/projectController/client";
import { getProjectByIdQueryKey } from "@synthesis/shared-services/project-gge/projectController/hooks";
import type { ProjectWithMetaDtoTypeEnumKey } from "@synthesis/shared-services/project-gge/projectController/types";
import { IconClose, IconEdit, IconSave } from "@synthesis/shared-icons";
import { Button, Input, Link, setSnack } from "@synthesis/shared-ui";
import { logger } from "@synthesis/shared-utils";

import { projectQueryKeys, PROJECT_EXPERTISE_CODE_MASK, formatProjectExpertiseCode } from "../../../../entity/project";
import type { InformationWidgetProps } from "../project-dashboard-page.lib";
import { ModalUpdateInformation } from "./modal-update-information";

type FormData = {
  code: string;
  objectName: string;
  description: string;
};

export const InformationWidget = ({
  project,
  isLoading,
  isIntegration,
  disabled,
  title,
  setIsEdit,
  isEdit,
  isEditOrganization,
}: InformationWidgetProps) => {
  const methods = useForm<FormData>();
  const queryClient = useQueryClient();

  if (isLoading || !project) {
    return <div className="bg-neutrals-90 animate-pulse rounded-[0.875rem]" />;
  }

  const metaCode = project.meta?.find((el: ProjectMetaDto) => el.key === "externalId")?.value;
  const formattedMetaCode = formatProjectExpertiseCode(metaCode);

  const onSubmit = async (formData: FormData) => {
    setIsEdit(false);
    const projectId = project.id;
    if (!projectId) return;

    try {
      if (metaCode !== formData.code) {
        await setMeta({
          path: { projectId, key: "externalId" },
          body: {
            key: "externalId",
            value: formData.code,
          },
        });
        await queryClient.invalidateQueries({ queryKey: getProjectMetaQueryKey({ path: { projectId } }) });
        await queryClient.invalidateQueries({ queryKey: projectQueryKeys.meta(projectId) });
      }
      await updateProject({
        body: {
          id: projectId,
          description: formData.description,
          objectName: formData.objectName,
          title: title ?? project.title,
          type: (project as { type?: ProjectWithMetaDtoTypeEnumKey }).type ?? "SIMPLE",
        },
      });
      await queryClient.invalidateQueries({ queryKey: getProjectByIdQueryKey({ path: { id: projectId } }) });
      await queryClient.invalidateQueries({ queryKey: projectQueryKeys.detail(projectId) });
    } catch (e) {
      const message = e instanceof Error ? e.message : "";
      if (message.includes("externalId")) {
        setSnack({
          title: "Невозможно изменить проект",
          description: `Проект с таким номером ${formData.code} уже существует`,
          messageType: "error",
        });
        methods.setValue("code", metaCode ?? "");
      }
      logger.error(e);
    }
  };

  const updateButton = isIntegration ? (
    <ModalUpdateInformation
      setIsEdit={setIsEdit}
      trigger={
        <Button disabled={project.isArchived || disabled} size="sm" onClick={() => setIsEdit(true)} leftIcon={<IconEdit className="size-5" />}>
          Редактировать
        </Button>
      }
      title={project.title ?? "-"}
    />
  ) : (
    <Button disabled={project.isArchived || disabled} size="sm" onClick={() => setIsEdit(true)} leftIcon={<IconEdit className="size-5" />}>
      Редактировать
    </Button>
  );

  return (
    <section className="w-full gap-4 rounded-xl bg-white p-6">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <header className="flex items-center justify-between pb-4">
            <h1 className="text-lg font-bold">Общая информация</h1>
            {!isEdit ? (
              updateButton
            ) : (
              <div className="flex gap-4">
                <Button size="sm" onClick={() => setIsEdit(false)} leftIcon={<IconClose className="size-5" />}>
                  Отменить
                </Button>
                <Button size="sm" leftIcon={<IconSave className="size-5" />} type="submit">
                  Сохранить
                </Button>
              </div>
            )}
          </header>
          <main className="flex flex-col gap-4">
            <div className="flex flex-col">
              <div className="flex">
                <div className="w-1/2">
                  <h2 className="subtitle-sm text-type-light pb-2">Номер проекта экспертизы</h2>
                  {!isEdit ? (
                    <p className="paragraph-base">{formattedMetaCode || "-"}</p>
                  ) : (
                    <Input
                      className="w-fit"
                      name="code"
                      type="text"
                      settings={{ mask: PROJECT_EXPERTISE_CODE_MASK }}
                      defaultValue={project.meta?.find((el: ProjectMetaDto) => el.key === "externalId")?.value || "-"}
                    />
                  )}
                </div>
                <div className="w-1/2">
                  <h2 className="subtitle-sm text-type-light pb-2">Наименование организации</h2>
                  {isEdit && isEditOrganization ? (
                    <Input className="w-fit" defaultValue={project.objectName || "-"} name="objectName" />
                  ) : (
                    <p className="paragraph-base">{project.objectName || "-"}</p>
                  )}
                </div>
              </div>
              <div className="pt-4">
                <h2 className="subtitle-sm text-type-light pb-2">Ссылка на документацию</h2>
                <p className="paragraph-base">
                  {!isEdit ? (
                    <Link to={project.description ?? ""}>{project.description}</Link>
                  ) : (
                    <Input className="w-full" defaultValue={project.description ?? ""} name="description" />
                  )}
                </p>
              </div>
            </div>
          </main>
        </form>
      </FormProvider>
    </section>
  );
};
